import { useEffect, useRef, useState } from 'react';
import _ from "lodash";

import { calculateOrderForWidgets } from 'components/Editor/helper/widget';
import { WidgetRegistry } from 'components/Editor/widgets/registry';

const DEFAULT_TEXT_FONT_SIZE = 16;

const normalizeTextContentForCompare = (content, fallbackFontSize = DEFAULT_TEXT_FONT_SIZE) => {
  if (typeof content !== 'string' || typeof document === 'undefined') {
    return content;
  }

  const root = document.createElement('div');
  root.innerHTML = content;

  root.querySelectorAll('p, h1, h2, h3, h4, h5, h6').forEach((block) => {
    const blockPx = parseFloat(block.style.fontSize);
    if (!blockPx) return;

    const innerMatch = block.innerHTML.match(/font-size:\s*([\d.]+)px/i);
    const innerPx = innerMatch ? parseFloat(innerMatch[1]) : null;
    const isFallbackSize = Math.abs(blockPx - fallbackFontSize) < 0.01;
    const matchesInnerSpan = innerPx != null && Math.abs(blockPx - innerPx) < 0.01;
    const isEmptyBlock = !block.textContent?.trim();

    if (isFallbackSize || matchesInnerSpan || isEmptyBlock) {
      block.style.removeProperty('font-size');
      if (!block.getAttribute('style')?.trim()) block.removeAttribute('style');
    }
  });

  return root.innerHTML;
};

const normalizeContent = (content, widget) => {
  if (content == null) {
    return content;
  }

  if (typeof content === 'object' && !Array.isArray(content) && content.id != null) {
    return content.id;
  }

  if (widget?.type === 'cleverstory.v2.widgets.text' && typeof content === 'string') {
    const fallbackFontSize =
      _.get(widget, 'properties.desktop.fontSize') ||
      _.get(widget, 'properties.mobile.fontSize') ||
      DEFAULT_TEXT_FONT_SIZE;

    return normalizeTextContentForCompare(content, fallbackFontSize);
  }

  return content;
};

const normalizeDimensionValue = (value) => {
  if (typeof value !== 'string') {
    return value;
  }

  const pxMatch = value.trim().match(/^([\d.]+)px$/i);
  if (pxMatch) {
    return `${Math.floor(parseFloat(pxMatch[1]))}px`;
  }

  return value;
};

const normalizeWidgetValue = (value) => {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value === 'string') {
    const normalizedDimension = normalizeDimensionValue(value);
    if (normalizedDimension !== value) {
      return normalizedDimension;
    }

    if (!isNaN(value) && value.trim() !== '') {
      return Number(value);
    }
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map(normalizeWidgetValue)
      .filter((item) => item !== undefined);

    return normalized.length ? normalized : undefined;
  }

  if (typeof value === 'object') {
    return normalizeWidgetObject(value);
  }

  return value;
};

const normalizeWidgetObject = (obj) => {
  if (!_.isObject(obj) || obj === null || Array.isArray(obj)) {
    return obj;
  }

  return Object.keys(obj).reduce((cleaned, key) => {
    const value = normalizeWidgetValue(obj[key]);

    const shouldKeep =
      value !== undefined &&
      !(key === 'type' && value === 'solid') &&
      !(key === 'blur' && value === 0) &&
      !(key === 'opacity' && value === 0) &&
      !(_.isObject(value) && !_.isArray(value) && _.isEmpty(value)) &&
      !(_.isArray(value) && value.length === 0);

    return shouldKeep ? { ...cleaned, [key]: value } : cleaned;
  }, {});
};

const prepareWidgetsForComparison = (widgets) => {
  const ordered = calculateOrderForWidgets([...widgets], null);

  const normalized = ordered.map((widget) =>
    _.omit(
      {
        ...widget,
        content: normalizeContent(widget.content, widget),
      },
      ['step', 'editorState']
    )
  );

  return WidgetRegistry.processRequest(normalized).map((widget) =>
    normalizeWidgetObject(_.omit(widget, ['widgets']))
  );
};

export const hasUnsavedChanges = ({
  currentWidgets,
  previousWidgets,
  menuDesigns,
  previousMenuDesigns,
  variableMappings,
  deletedVariableMappings,
}) => {

  const normalizeMediaReference = (media) => {
    if (media == null) {
      return media;
    }

    if (typeof media === "object" && media.id != null) {
      return media.id;
    }

    return media;
  };

  const normalizeAppearance = (obj) => {
    if (_.isString(obj) && !isNaN(obj) && obj.trim() !== "") {
      return Number(obj);
    }

    if (_.isArray(obj)) {
      return obj
        .map(normalizeAppearance)
        .filter((item) => item !== undefined);
    }

    if (!_.isObject(obj) || obj === null) {
      return obj;
    }

    return Object.keys(obj).reduce((cleaned, key) => {
      const rawValue = obj[key];
      const value =
        key === "media"
          ? normalizeMediaReference(rawValue)
          : normalizeAppearance(rawValue);

      const shouldKeep =
        value != null &&
         !(key === "type" && value === "solid") &&
        !(key === "blur" && value === 0) &&
        !(key === "opacity" && value === 0) &&
        !(_.isObject(value) && !_.isArray(value) && _.isEmpty(value)) &&
        !(_.isArray(value) && value.length === 0);

      return shouldKeep
        ? { ...cleaned, [key]: value }
        : cleaned;
    }, {});
  };

  currentWidgets = prepareWidgetsForComparison(currentWidgets);
  previousWidgets = prepareWidgetsForComparison(previousWidgets);
  let remainingPreviousWidgets = [...previousWidgets];

  const changedWidgets = currentWidgets.filter((widget) => {
    const previous = remainingPreviousWidgets.find((prevWidget) => {
      return _.isEqual(prevWidget, widget);
    });

    if (previous) {
      remainingPreviousWidgets = remainingPreviousWidgets.filter(
        (item) => item !== previous
      );
    }

    return previous == null;
  });

  const deletedWidgets = remainingPreviousWidgets.filter((prevWidget) => {
    const deleted = currentWidgets.find((widget) => {
      return _.isEqual(
        _.get(prevWidget, "id"),
        _.get(widget, "id")
      );
    });

    return deleted == null;
  });

  const hasAppearanceChanges = !_.isEqual(
    normalizeAppearance(menuDesigns),
    normalizeAppearance(previousMenuDesigns)
  );

  const hasPendingVariableMappings = (variableMappings || []).some(
    (mapping) => mapping && !mapping.id
  );

  const hasVariableMappingChanges =
    hasPendingVariableMappings || _.size(deletedVariableMappings) > 0;

  return (
    _.size(changedWidgets) > 0 ||
    _.size(deletedWidgets) > 0 ||
    hasAppearanceChanges ||
    hasVariableMappingChanges
  );
};

const BASELINE_CAPTURE_DELAY_MS = 100;

export function useEditorUnsavedChanges({
  id,
  entityType,
  currentWidgets,
  menuDesigns,
  draftWidgets,
  draftMenuDesigns,
  variableMappings,
  deletedVariableMappings,
  useEditorBaseline = false,
  loadingEntity = false,
  entityEditorReady = true,
  updateSuccess = false,
  restoreSuccess = false,
}) {
  const [baseline, setBaseline] = useState(null);
  const capturedRef = useRef(false);
  const saveSyncedRef = useRef(false);
  const snapshotRef = useRef({ widgets: currentWidgets, menuDesigns });

  snapshotRef.current = { widgets: currentWidgets, menuDesigns };

  const resetBaseline = () => {
    capturedRef.current = false;
    saveSyncedRef.current = false;
    setBaseline(null);
  };

  useEffect(() => {
    resetBaseline();
  }, [id]);

  useEffect(() => {
    if (!restoreSuccess) {
      return;
    }

    resetBaseline();
  }, [restoreSuccess]);

  useEffect(() => {
    if (
      !useEditorBaseline ||
      loadingEntity ||
      !entityEditorReady ||
      !currentWidgets?.length
    ) {
      return;
    }

    if (capturedRef.current) {
      return;
    }

    const timer = setTimeout(() => {
      capturedRef.current = true;
      const snapshot = snapshotRef.current;
      setBaseline({
        id,
        widgets: snapshot.widgets,
        menuDesigns: snapshot.menuDesigns,
      });
    }, BASELINE_CAPTURE_DELAY_MS);

    return () => clearTimeout(timer);
  }, [
    useEditorBaseline,
    id,
    loadingEntity,
    entityEditorReady,
    currentWidgets,
    menuDesigns,
  ]);

  useEffect(() => {
    if (!updateSuccess) {
      saveSyncedRef.current = false;
      return;
    }

    if (
      saveSyncedRef.current ||
      !useEditorBaseline ||
      !currentWidgets?.length
    ) {
      return;
    }

    saveSyncedRef.current = true;
    const snapshot = snapshotRef.current;
    setBaseline({
      id,
      widgets: snapshot.widgets,
      menuDesigns: snapshot.menuDesigns,
    });
  }, [updateSuccess, useEditorBaseline, id, currentWidgets?.length]);

  const baselineReady = !useEditorBaseline || baseline?.id === id;
  const waitingForBaseline =
    useEditorBaseline && (loadingEntity || !baselineReady);

  const mappingArgs =
    entityType === 'GATING'
      ? { variableMappings: [], deletedVariableMappings: [] }
      : { variableMappings, deletedVariableMappings };

  const hasChanges = useEditorBaseline
    ? baselineReady &&
      hasUnsavedChanges({
        currentWidgets,
        previousWidgets: baseline?.widgets,
        menuDesigns,
        previousMenuDesigns: baseline?.menuDesigns,
        ...mappingArgs,
      })
    : hasUnsavedChanges({
        currentWidgets,
        previousWidgets: draftWidgets,
        menuDesigns,
        previousMenuDesigns: draftMenuDesigns,
        ...mappingArgs,
      });

  return { hasChanges, baselineReady, waitingForBaseline };
}
